/**
 * El frontend de MGB.
 *
 * Por ahora es el esqueleto: la pantalla de ingreso funciona de verdad
 * contra la API, y las secciones están dibujadas pero vacías, cada una
 * indicando en qué fase se construye. A medida que avancen las fases, cada
 * sección se va llenando.
 *
 * Sin librerías de afuera: HTML, CSS y JavaScript del navegador.
 */

import { formatearPesosCortos, formatearKm, formatearPeriodo } from './formato.js';
import { cargarDefiniciones, pantallaDeMaestro } from './maestros.js';
import { pantallaDeConfiguracion } from './configuracion.js';
import { pantallaDeCambioDeClave } from './cambiar-clave.js';
import { pantallaDeViajes, pantallaDeNuevoViaje } from './viajes.js';
import { pantallaDeGastos } from './gastos.js';
import { pantallaDeCuentaCorriente } from './cuenta.js';
import { pantallaDeCheques } from './cheques.js';
import { pantallaDeLiquidacion } from './liquidacion.js';
import { esPantallaDeRecuperacion, pantallaDeRecuperacion } from './recuperar.js';

const $ = (sel) => document.querySelector(sel);

/**
 * Versión del frontend. Se muestra en pantalla para poder confirmar, sin
 * abrir las herramientas del navegador, QUÉ versión está corriendo. Si acá
 * dice una versión vieja, el navegador tiene guardada la anterior.
 */
// Ojo: __VERSION__ va SIN comillas. La compilación reemplaza identificadores,
// no texto entre comillas: con comillas quedaba el literal "__VERSION__" y el
// testigo mostraba una versión falsa. Lo encontró el test del bundle.
export const VERSION =
  typeof __VERSION__ === 'undefined' ? 'sin compilar' : __VERSION__;

// Testigo: si el JavaScript no se ejecuta, este renglón queda en
// "cargando…" y el formulario se va a enviar solo. Es la señal de alarma.
const testigo = $('#testigo-js');
if (testigo) {
  testigo.textContent = `JavaScript activo · versión ${VERSION}`;
  testigo.className = 'testigo bien';
}

// ---------------------------------------------------------------------
// Hablar con la API
// ---------------------------------------------------------------------
async function pedir(ruta, opciones = {}) {
  // El tipo de contenido se declara SÓLO si va contenido. Declarar
  // "application/json" con el cuerpo vacío hace que el servidor conteste
  // 400. Fue exactamente lo que rompió el botón Salir: el pedido fallaba,
  // la página recargaba, la sesión seguía abierta y parecía que el botón
  // parpadeaba sin hacer nada.
  //
  // Con un archivo adjunto es al revés: el tipo lo tiene que poner el
  // NAVEGADOR, porque incluye una marca separadora que él inventa. Si se lo
  // pisáramos con "application/json", el servidor no encontraría el archivo.
  const cabeceras = opciones.body && !(opciones.body instanceof FormData)
    ? { 'Content-Type': 'application/json' } : {};
  const respuesta = await fetch(ruta, {
    credentials: 'same-origin',
    cache: 'no-store',
    ...opciones,
    headers: { ...cabeceras, ...(opciones.headers ?? {}) },
  });
  let cuerpo = null;
  try { cuerpo = await respuesta.json(); } catch { /* puede no traer cuerpo */ }
  return { ok: respuesta.ok, estado: respuesta.status, cuerpo };
}

// ---------------------------------------------------------------------
// Avisos: SIEMPRE con símbolo y texto, nunca sólo color
// ---------------------------------------------------------------------
function alerta(tipo, texto) {
  const simbolos = { critico: '!', aviso: '!', bien: '✓' };
  const div = document.createElement('div');
  div.className = `alerta ${tipo}`;
  div.setAttribute('role', tipo === 'critico' ? 'alert' : 'status');
  div.innerHTML = `<span class="simbolo" aria-hidden="true">${simbolos[tipo] ?? '·'}</span><span></span>`;
  div.lastElementChild.textContent = texto;
  return div;
}

function mostrarErrorIngreso(texto) {
  const caja = $('#error-ingreso');
  caja.innerHTML = '';
  if (texto) { caja.appendChild(alerta('critico', texto)); caja.hidden = false; }
  else { caja.hidden = true; }
}

// ---------------------------------------------------------------------
// Ingreso
// ---------------------------------------------------------------------
$('#formulario-ingreso').addEventListener('submit', async (evento) => {
  evento.preventDefault();
  mostrarErrorIngreso(null);

  const email = $('#email').value.trim();
  const clave = $('#clave').value;
  if (!email || !clave) {
    mostrarErrorIngreso('Escribí tu email y tu contraseña.');
    return;
  }

  const boton = $('#boton-entrar');
  boton.disabled = true;
  boton.textContent = 'Entrando…';
  try {
    const r = await pedir('/api/login', { method: 'POST', body: JSON.stringify({ email, clave }) });
    if (r.ok) { $('#clave').value = ''; await arrancar(); return; }
    mostrarErrorIngreso(r.cuerpo?.error ?? 'No se pudo entrar. Probá de nuevo.');
  } catch {
    mostrarErrorIngreso('No se pudo conectar con el servidor.');
  } finally {
    boton.disabled = false;
    boton.textContent = 'Entrar';
  }
});

// ---------------------------------------------------------------------
// Las secciones
// ---------------------------------------------------------------------
const ROLES = {
  dueno: 'Dueño', administrativo: 'Administrativo',
  contador: 'Contador', chofer: 'Chofer',
};

/**
 * Qué mostrar en cada sección.
 *
 * Los nombres y las fases NO están acá: vienen como atributos de los
 * botones del menú, que llegan con el armazón (que sólo se entrega con
 * sesión). Este archivo es público, así que no lleva la estructura del
 * sistema, sólo el comportamiento.
 */
const PANTALLAS = {
  inicio: dibujarInicio,
  configuracion: dibujarConfiguracion,
  'cambiar-clave': dibujarCambioDeClave,
  viajes: (contenedor) => pantallaDeViajes(contenedor, { pedir }),
  'nuevo-viaje': (contenedor) => pantallaDeNuevoViaje(contenedor, { pedir }),
  gastos: (contenedor) => pantallaDeGastos(contenedor, { pedir }),
  cuenta: (contenedor) => pantallaDeCuentaCorriente(contenedor, { pedir }),
  cheques: (contenedor) => pantallaDeCheques(contenedor, { pedir }),
  liquidacion: (contenedor) => pantallaDeLiquidacion(contenedor, { pedir, rol: ROL_ACTUAL }),
};

let ROL_ACTUAL = null;

async function dibujarConfiguracion(contenedor) {
  await pantallaDeConfiguracion(contenedor, { pedir, rol: ROL_ACTUAL });
}

function dibujarCambioDeClave(contenedor) {
  pantallaDeCambioDeClave(contenedor, { pedir, obligatorio: false });
}

async function dibujarMaestro(contenedor, nombre) {
  const definiciones = await cargarDefiniciones(pedir);
  const definicion = definiciones[nombre];
  if (!definicion) {
    contenedor.appendChild(alerta('critico', 'No se pudo traer la definición de esta pantalla.'));
    return;
  }
  pantallaDeMaestro(contenedor, { ruta: nombre, definicion, pedir });
}

function periodoActual() {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

function tarjetaCifra(rotulo, cifra, pie) {
  return `<div class="tarjeta">
    <div class="rotulo">${rotulo}</div>
    <div class="cifra">${cifra}</div>
    <div class="pie-cifra">${pie}</div>
  </div>`;
}

function dibujarInicio(contenedor) {
  // Todavía no hay datos: los cuatro números van en cero y se dice por qué.
  const cero = 0n;
  contenedor.appendChild(alerta('aviso',
    'Todavía no hay datos cargados. Los números van a aparecer cuando se ' +
    'carguen los camiones, los choferes y el primer viaje.'));

  const tarjetas = document.createElement('div');
  tarjetas.className = 'tarjetas';
  tarjetas.innerHTML = [
    tarjetaCifra('Facturado del mes',    formatearPesosCortos(cero), formatearPeriodo(periodoActual())),
    tarjetaCifra('Por cobrar',           formatearPesosCortos(cero), 'sin facturas pendientes'),
    tarjetaCifra('Resultado del mes',    formatearPesosCortos(cero), 'ingresos menos egresos'),
    tarjetaCifra('Km recorridos',        formatearKm(cero),          'en el mes'),
  ].join('');
  contenedor.appendChild(tarjetas);

  const ultimos = document.createElement('div');
  ultimos.className = 'tarjeta';
  ultimos.style.marginTop = '18px';
  ultimos.innerHTML = `
    <h2 style="font-size:15px;margin:0 0 12px">Últimos viajes</h2>
    <div class="tabla-scroll">
      <table class="datos">
        <thead>
          <tr>
            <th>Viaje</th><th>Cliente</th><th>Recorrido</th>
            <th class="numero">Km</th><th class="plata">Flete neto</th><th>Estado</th>
          </tr>
        </thead>
        <tbody>
          <tr><td colspan="6" style="color:var(--texto-suave);text-align:center;padding:22px">
            Todavía no se cargó ningún viaje.
          </td></tr>
        </tbody>
      </table>
    </div>`;
  contenedor.appendChild(ultimos);
}

function dibujarPendiente(contenedor, fase) {
  contenedor.appendChild(alerta('aviso',
    `Esta pantalla se construye en la fase ${fase ?? '?'}. Todavía no está hecha.`));
}

function irA(nombre) {
  const boton = document.querySelector(`#menu button[data-seccion="${nombre}"]`)
             ?? document.querySelector('#menu button[data-seccion="inicio"]');
  if (!boton) return;
  const { titulo, fase, maestro, pantalla } = boton.dataset;

  $('#titulo').textContent = titulo ?? 'Inicio';
  $('#subtitulo').textContent = formatearPeriodo(periodoActual());

  for (const b of document.querySelectorAll('#menu button')) {
    if (b === boton) b.setAttribute('aria-current', 'page');
    else b.removeAttribute('aria-current');
  }

  const contenedor = $('#contenido');
  contenedor.innerHTML = '';
  if (maestro) void dibujarMaestro(contenedor, maestro);
  else if (pantalla && PANTALLAS[pantalla]) void PANTALLAS[pantalla](contenedor);
  else if (PANTALLAS[nombre]) void PANTALLAS[nombre](contenedor);
  else dibujarPendiente(contenedor, fase);
}

// ---------------------------------------------------------------------
// Arranque
// ---------------------------------------------------------------------
async function arrancar() {
  // La recuperación va ANTES de preguntar por la sesión: quien llega acá no
  // tiene sesión, y pedirla primero mostraría el ingreso por un instante.
  if (esPantallaDeRecuperacion()) {
    // Se dibuja dentro de la caja del ingreso: misma marca, mismo lugar. El
    // formulario de ingreso se saca del DOM, no se esconde.
    $('#pantalla-ingreso').hidden = false;
    $('#aplicacion').innerHTML = '';
    const caja = $('#pantalla-ingreso .caja');
    const marca = caja.querySelector('.marca');
    caja.innerHTML = '';
    if (marca) caja.appendChild(marca);
    const hueco = document.createElement('div');
    caja.appendChild(hueco);
    pantallaDeRecuperacion(hueco, { pedir });
    return;
  }

  const yo = await pedir('/api/yo');

  if (!yo.ok) {
    // Sin sesión: sólo el ingreso. La aplicación ni se pide.
    mostrarSoloIngreso();
    return;
  }

  ROL_ACTUAL = yo.cuerpo?.rol ?? null;

  // Si todavía no cambió la contraseña que le dieron, no se carga NADA de la
  // aplicación: sólo la pantalla para elegir una nueva. El servidor rechaza
  // igual cualquier otro pedido, así que esto no es la única protección.
  if (yo.cuerpo?.debeCambiarClave) {
    mostrarCambioObligatorio();
    return;
  }

  // Con sesión: recién ahora se pide el armazón de la aplicación. El
  // servidor lo entrega únicamente si la sesión es válida; si no, no manda
  // nada y acá se vuelve al ingreso.
  const pantalla = await fetch('/api/pantalla', { credentials: 'same-origin' });
  if (!pantalla.ok) {
    mostrarSoloIngreso();
    return;
  }

  const contenedor = $('#aplicacion');
  contenedor.innerHTML = await pantalla.text();
  contenedor.className = 'app';

  $('#pantalla-ingreso').hidden = true;
  $('#quien-soy').textContent = ROLES[ROL_ACTUAL] ?? 'Usuario';

  $('#boton-salir').addEventListener('click', salir);
  $('#menu').addEventListener('click', (evento) => {
    const boton = evento.target.closest('button[data-seccion]');
    if (boton) irA(boton.dataset.seccion);
  });

  irA('inicio');
}

/**
 * La primera vez: elegir la contraseña antes de ver nada.
 *
 * No se pide el armazón de la aplicación, así que el menú y las secciones ni
 * llegan al navegador hasta que la contraseña esté cambiada.
 */
function mostrarCambioObligatorio() {
  $('#pantalla-ingreso').hidden = true;
  const contenedor = $('#aplicacion');
  contenedor.className = 'ingreso';
  contenedor.innerHTML = '<div class="caja" id="caja-cambio"></div>';
  pantallaDeCambioDeClave($('#caja-cambio'), {
    pedir,
    obligatorio: true,
    // Al terminar se arranca de nuevo: ahora sí entra al sistema.
    alTerminar: arrancar,
  });
}

/** Deja la página como si nunca se hubiera entrado: sin rastro de la app. */
function mostrarSoloIngreso() {
  const contenedor = $('#aplicacion');
  contenedor.innerHTML = '';        // se saca del DOM, no se esconde
  contenedor.className = '';
  $('#pantalla-ingreso').hidden = false;
  $('#email')?.focus();
}

async function salir() {
  const boton = $('#boton-salir');
  if (boton) { boton.disabled = true; boton.textContent = 'Saliendo…'; }

  const r = await pedir('/api/salir', { method: 'POST' });
  if (!r.ok) {
    // No se pudo cerrar del lado del servidor: NO se hace como si hubiera
    // salido. Es peor dejar creer que se cerró la sesión cuando sigue abierta.
    if (boton) { boton.disabled = false; boton.textContent = 'Salir'; }
    alert('No se pudo cerrar la sesión. Probá de nuevo. Si sigue fallando, ' +
          'cerrá el navegador y avisá.');
    return;
  }

  // Se saca la aplicación del DOM antes de recargar, para que no quede ni un
  // instante a la vista, y se usa replace() para que la aplicación no quede
  // en el historial: volver atrás no la trae de vuelta.
  mostrarSoloIngreso();
  location.replace(location.pathname);
}

/**
 * Volver atrás con el botón del navegador.
 *
 * El navegador guarda la página entera en memoria y al volver atrás la
 * restaura tal cual estaba, SIN volver a ejecutar nada. Si no se hiciera
 * nada, después de salir se podría volver atrás y ver la aplicación otra
 * vez, con los datos en pantalla, aunque la sesión ya no valga.
 *
 * Por eso, cada vez que la página se muestra —incluso restaurada de la
 * memoria del navegador— se vuelve a preguntar si la sesión sigue valiendo.
 */
window.addEventListener('pageshow', (evento) => {
  if (evento.persisted) void arrancar();
});

// Y al volver de otra pestaña, por las dudas.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && document.querySelector('#menu')) {
    void pedir('/api/yo').then((r) => { if (!r.ok) mostrarSoloIngreso(); });
  }
});

// ---------------------------------------------------------------------
// Cuando algo se rompe en la pantalla, SE TIENE QUE NOTAR
// ---------------------------------------------------------------------
//
// EL DÍA QUE PASÓ (27/10/2026): el cliente no podía cargar clientes. Apretaba
// Guardar y, en sus palabras, "no pasa nada". Desde el servidor no se veía
// NADA, porque el pedido nunca salía de su navegador. Reproducirlo acá
// funcionaba perfecto, así que no quedaba más que adivinar.
//
// Un error que no se ve es peor que uno que se ve: la persona cree que el
// sistema la ignora, vuelve a intentar, y pierde el trabajo. Ahora pasan dos
// cosas siempre: se muestra un cartel, y queda anotado en el servidor con la
// línea exacta — sin pedirle a nadie que abra una consola que nunca vio.
let avisando = false;

function avisarDeLaRotura(mensaje, pila) {
  // El cartel va PRIMERO: si el aviso al servidor fallara, la persona igual
  // tiene que enterarse de que su acción no se hizo.
  try {
    const caja = document.createElement('div');
    caja.className = 'alerta critico';
    caja.setAttribute('role', 'alert');
    caja.style.cssText = 'position:fixed;left:12px;right:12px;bottom:12px;z-index:9999;' +
                         'box-shadow:0 6px 24px rgba(0,0,0,.25)';
    caja.innerHTML = '<span class="simbolo" aria-hidden="true">!</span><span></span>';
    caja.lastElementChild.textContent =
      'Algo se rompió en esta pantalla y lo que apretaste NO se hizo. Ya quedó avisado. ' +
      'Recargá la página (F5) y, si vuelve a pasar, contá qué estabas haciendo.';
    document.body.appendChild(caja);
    setTimeout(() => caja.remove(), 30000);
  } catch { /* si ni esto se puede, queda el aviso al servidor */ }

  if (avisando) return;          // un error no puede disparar una catarata
  avisando = true;
  setTimeout(() => { avisando = false; }, 5000);

  try {
    // Se manda sin pasar por pedir(): si lo que se rompió fue pedir(), este
    // aviso tiene que salir igual.
    void fetch('/api/aviso-de-pantalla', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mensaje: String(mensaje ?? '').slice(0, 300),
        pila: String(pila ?? '').slice(0, 1200),
        donde: location.pathname + location.search,
        pantalla: document.querySelector('#titulo')?.textContent ?? '',
      }),
    }).catch(() => { /* ya se mostró el cartel */ });
  } catch { /* ídem */ }
}

window.addEventListener('error', (e) => {
  avisarDeLaRotura(e.message, e.error?.stack ?? `${e.filename}:${e.lineno}:${e.colno}`);
});

// La que más importa acá: un guardado es una función async, y si revienta, la
// promesa queda rechazada sin que nadie la mire. Eso era exactamente
// "aprieto y no pasa nada".
window.addEventListener('unhandledrejection', (e) => {
  const r = e.reason;
  avisarDeLaRotura(r?.message ?? String(r), r?.stack ?? '');
});

arrancar();
